'use client';

// Core
import { useState, useTransition } from 'react';

// Local component
import { CardWrapper } from './card-wrapper';

// Vendors
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

// Global schemas
import { ResetPasswordSchema } from '@/schemas';

// Global UI components
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { FormError, FormSuccess } from '@/components/form';
import { resetPassword } from '@/actions';
import { LoadingSpinner } from '@/components/animations/loading-spinner';
import { useTranslations } from '@/hooks/useTranslations';

export const ResetPasswordForm = () => {
  const [error, setError] = useState<string | undefined>('');
  const [success, setSuccess] = useState<string | undefined>('');

  const [isPending, startTransition] = useTransition();

  const form = useForm<z.infer<typeof ResetPasswordSchema>>({
    resolver: zodResolver(ResetPasswordSchema),
    defaultValues: {
      email: '',
    },
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const onSubmit = (values: z.infer<typeof ResetPasswordSchema>) => {
    setError('');
    setSuccess('');

    startTransition(() => {
      resetPassword(values)
        .then((data) => {
          if (data?.error) {
            setError(data.error);
          }

          if (data?.success) {
            form.reset();
            setSuccess(data.success);
          }
        })
        .catch(() => setError('Something went wrong!'));
    });
  };

  const t = useTranslations('LoginForm');

  return (
    <CardWrapper
      title="Forgot your password?"
      headerLabel="Please enter your email and we will send you an email for resetting your password!"
      backButtonLabel="Back to login"
      backButtonHref="/login"
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>

                  <FormControl>
                    <Input
                      {...field}
                      disabled={isPending}
                      placeholder="john.doe@example.com"
                      type="email"
                      className={
                        fieldState.error
                          ? 'border-red-600 focus-visible:ring-red-600'
                          : ''
                      }
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormError message={error} />
          <FormSuccess message={success} />

          <Button
            type="submit"
            className="w-full"
            disabled={isPending}
            aria-label="Reset email"
          >
            {isPending ? <LoadingSpinner /> : t('resetEmailLabel')}
          </Button>
        </form>
      </Form>
    </CardWrapper>
  );
};
